// MPR.js - Monthly Progress Reports
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TableContainer,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  Select,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
  InputBase,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useNavigate, useLocation } from "react-router-dom";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import CustomTooltip from "../../components/CustomTooltip";
import { useAuth } from "../../context/authContext";
import axios from "axios";
import { url } from "../../constants/url";
import { format, startOfMonth, endOfMonth, addMonths, subMonths, parseISO } from "date-fns";

const statusStyles = {
  "On Track": {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  "Report Missing": {
    bgcolor: "#FF6961",
    color: "#ffffff",
  },
  "DRAFT": {
    bgcolor: "#F8D66D",
    color: "#ffffff",
  },
  "SUBMITTED": {
    bgcolor: "#FFB54C",
    color: "#ffffff",
  },
  "APPROVED": {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  "REJECTED": {
    bgcolor: "#FF6961",
    color: "#ffffff",
  },
  "ONTRACK": {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  "REPORTMISSING": {
    bgcolor: "#FF6961",
    color: "#ffffff",
  },
};

// Updated headers for MPR
const headers = [
  { key: "Project Name", sortable: true, field: "projectName" },
  { key: "Start Date", sortable: true, field: "projectStartDate" },
  { key: "End Date", sortable: true, field: "projectEndDate" },
  { key: "Engineer", sortable: true, field: "assignedEngineerName" },
  { key: "MPR Month", sortable: true, field: "monthStartDate" },
  { key: "Status", sortable: false, field: "projectStatus" },
  { key: "Actions", sortable: false },
];

const MPR = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;

  // Get auth context
  const { token, userData, loading: authLoading } = useAuth() || {};

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Check user role
  const isEngineerRole = userData?.role === "ENGINEER";
  const isAdminOrSuperAdmin = userData?.role === "ADMIN" || userData?.role === "SUPERADMIN";

  // State for data and loading
  const [mprs, setMprs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMprs, setTotalMprs] = useState(0);
  const [pageSize] = useState(10);

  // State for UI controls
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);

  // Sort and filter states
  const [sortConfig, setSortConfig] = useState(() => {
    const savedSort = localStorage.getItem("mprSort");
    return savedSort ? JSON.parse(savedSort) : {
      field: "monthStartDate",
      direction: "desc"
    };
  });

  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem("mprFilters");
    return savedFilters ? JSON.parse(savedFilters) : {
      search: "",
    };
  });

  const [selectedProjects, setSelectedProjects] = useState(state?.projectId ? [state.projectId] : []);
  const [selectedEngineers, setSelectedEngineers] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [showFilters, setShowFilters] = useState(!!state?.projectId);

  // Month filter states
  const [monthFilter, setMonthFilter] = useState("ALL");
  const [customMonthRange, setCustomMonthRange] = useState({
    from: null,
    to: null,
  });
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  // Filter menu states
  const [filterMenusOpen, setFilterMenusOpen] = useState({
    projects: false,
    engineers: false,
    statuses: false,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Download loading state
  const [downloadingId, setDownloadingId] = useState(null);

  // Save sort config to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("mprSort", JSON.stringify(sortConfig));
  }, [sortConfig]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("mprFilters", JSON.stringify(filters));
  }, [filters]);

  // Fetch projects for filter dropdown
  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${url}/api/inhouse-projects/fetch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          limit: 1000,
          populate: "assignedEngineer"
        }
      });

      if (response.data.projects) {
        console.log("Projects with engineer data:", response.data.projects);
        setProjects(response.data.projects);
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
    }
  }, [token]);

  // Fetch engineers for filter dropdown
  const fetchEngineers = useCallback(async () => {
    try {
      const response = await axios.get(`${url}/api/users/fetch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { role: "ENGINEER", limit: 1000 }
      });

      if (response.data.users) {
        setEngineers(response.data.users);
      }
    } catch (err) {
      console.error("Error fetching engineers:", err);
    }
  }, [token]);

  // Fetch MPRs with project status from your backend API
  const fetchMPRs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const params = {
        page: page,
        limit: pageSize,
      };

      // Add search parameter
      if (filters.search && filters.search.trim() !== "") {
        params.search = filters.search.trim();
      }

      // Add project filter (multiple IDs as comma-separated string)
      if (selectedProjects.length > 0) {
        params.projectId = selectedProjects.join(",");
      }

      // Add engineer filter (multiple IDs as comma-separated string)
      if (selectedEngineers.length > 0) {
        params.userId = selectedEngineers.join(",");
      }

      // Add project status filter
      const projectStatuses = selectedStatuses.filter(s =>
        ["ONTRACK", "REPORTMISSING"].includes(s)
      );

      // Handle month filter options
      if (monthFilter !== "ALL") {
        let fromDate = null;
        let toDate = null;

        const today = new Date();

        switch (monthFilter) {
          case "THIS_MONTH":
            fromDate = startOfMonth(today);
            toDate = endOfMonth(today);
            break;
          case "LAST_MONTH":
            const lastMonth = subMonths(today, 1);
            fromDate = startOfMonth(lastMonth);
            toDate = endOfMonth(lastMonth);
            break;
          case "NEXT_MONTH":
            const nextMonth = addMonths(today, 1);
            fromDate = startOfMonth(nextMonth);
            toDate = endOfMonth(nextMonth);
            break;
          case "CUSTOM":
            if (customMonthRange.from && customMonthRange.to) {
              fromDate = new Date(customMonthRange.from);
              fromDate.setHours(0, 0, 0, 0);
              toDate = new Date(customMonthRange.to);
              toDate.setHours(23, 59, 59, 999);
            }
            break;
        }

        if (fromDate && toDate) {
          params.monthStartDate = fromDate.toISOString();
          params.monthEndDate = toDate.toISOString();
        }
      }

      // Add sort parameters based on sortConfig
      let sortParam = "monthDesc";
      if (sortConfig.field === "projectName") {
        sortParam = sortConfig.direction === "asc" ? "projectAz" : "projectZa";
      } else if (sortConfig.field === "monthStartDate") {
        sortParam = sortConfig.direction === "asc" ? "monthAsc" : "monthDesc";
      } else if (sortConfig.field === "assignedEngineerName") {
        sortParam = sortConfig.direction === "asc" ? "titleAsc" : "titleDesc";
      } else if (sortConfig.field === "createdAt") {
        sortParam = sortConfig.direction === "asc" ? "oldest" : "latest";
      }

      params.sortBy = sortConfig.field;
      params.sortOrder = sortConfig.direction === "asc" ? "asc" : "desc";

      console.log("Fetching MPRs with params:", params);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: params,
      };

      // Use your MPR backend API endpoint
      const response = await axios.get(`${url}/api/mpr/fetch`, config);

      console.log("MPR API Response:", response.data);

      if (response.data.mprs) {
        // Process MPR data
        const mprsWithDetails = response.data.mprs.map(mpr => {
          console.log("MPR data:", mpr);

          // Get project data
          const project = mpr.projectId;

          // Get engineer name
          let assignedEngineerName = "Unassigned";
          if (project?.assignedEngineerName) {
            assignedEngineerName = project.assignedEngineerName;
          } else if (project?.assignedEngineer) {
            if (typeof project.assignedEngineer === 'object') {
              assignedEngineerName = project.assignedEngineer.name || "Unassigned";
            }
          }

          // Calculate project status - prioritizing project.status if available
          const projectStatus = project?.status || determineProjectStatus(mpr);

          return {
            ...mpr,
            projectName: project?.projectName || mpr.projectName || "N/A",
            siteAddress: project?.siteAddress || mpr.siteAddress || "-",
            projectStartDate: project?.projectStartDate || mpr.projectStartDate,
            projectEndDate: project?.projectEndDate || mpr.projectEndDate,
            assignedEngineerName: assignedEngineerName,
            projectStatus: projectStatus,
            mprStatus: mpr.status,
          };
        });

        console.log("Processed MPRs:", mprsWithDetails);

        // Apply project status filter
        const filteredMprs = mprsWithDetails.filter(mpr => {
          if (projectStatuses.length === 0) return true;
          return projectStatuses.includes(mpr.projectStatus);
        });

        setMprs(filteredMprs);
        setTotalMprs(response.data.pagination?.total || filteredMprs.length);
        setTotalPages(response.data.pagination?.totalPages || Math.ceil((response.data.pagination?.total || filteredMprs.length) / pageSize));
      } else {
        setMprs([]);
        setTotalMprs(0);
        setTotalPages(1);
      }

    } catch (err) {
      console.error("Error fetching MPRs:", err);
      setError("Failed to fetch MPR data");
      showSnackbar("Failed to fetch MPR data. Please try again.", "error");
      setMprs([]);
      setTotalMprs(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [
    page, pageSize, token, sortConfig, filters,
    selectedProjects, selectedEngineers, selectedStatuses,
    monthFilter, customMonthRange
  ]);

  // Helper function to determine project status for MPR
  const determineProjectStatus = (mpr) => {
    // Logic similar to WPR:
    // 1. If MPR exists with status "SUBMITTED" or "APPROVED", project is ONTRACK
    // 2. If MPR exists with status "DRAFT", project is ONTRACK but draft exists
    // 3. If no MPR for expected month, project is REPORTMISSING
    // For now, using mpr.status as indicator

    if (!mpr) return "REPORTMISSING";

    const status = mpr.status?.toUpperCase();
    if (status === "SUBMITTED" || status === "APPROVED") {
      return "ONTRACK";
    } else if (status === "DRAFT") {
      return "ONTRACK"; // Or you could create a "DRAFT" status
    } else {
      return "REPORTMISSING";
    }
  };

  // Initial fetch - only when auth is loaded and user data is available
  useEffect(() => {
    if (!authLoading && userData && token) {
      fetchMPRs();
      fetchProjects();
      fetchEngineers();
    }
  }, [fetchMPRs, fetchProjects, fetchEngineers, authLoading, userData, token]);

  // Check if any filter is applied
  const isFilterApplied = () => {
    return (
      filters.search !== "" ||
      selectedProjects.length > 0 ||
      selectedEngineers.length > 0 ||
      selectedStatuses.length > 0 ||
      monthFilter !== "ALL" ||
      sortConfig.field !== "monthStartDate" ||
      sortConfig.direction !== "desc"
    );
  };

  // Handle sorting
  const handleSort = (field) => {
    const newSortConfig = {
      field,
      direction: sortConfig.field === field && sortConfig.direction === "asc" ? "desc" : "asc"
    };
    setSortConfig(newSortConfig);
    setPage(1);
  };

  // Get sort icon for a specific field
  const getSortIcon = (field) => {
    if (sortConfig.field === field) {
      return sortConfig.direction === "asc" ? "foldUp" : "foldDown";
    }
    return "sort";
  };

  // Handle global sort selection
  const handleGlobalSort = (sortType) => {
    let field, direction;

    switch (sortType) {
      case "LATEST":
        field = "monthStartDate";
        direction = "desc";
        break;
      case "OLDEST":
        field = "monthStartDate";
        direction = "asc";
        break;
      case "AZ":
        field = "projectName";
        direction = "asc";
        break;
      case "ZA":
        field = "projectName";
        direction = "desc";
        break;
      default:
        field = "monthStartDate";
        direction = "desc";
    }

    setSortConfig({ field, direction });
    setSortAnchorEl(null);
    setPage(1);
  };

  // Handle month filter selection
  const handleMonthFilterSelect = (filterValue) => {
    if (filterValue === "CUSTOM") {
      setShowMonthPicker(true);
      setCalendarAnchorEl(null);
    } else {
      setMonthFilter(filterValue);
      setCalendarAnchorEl(null);
      setPage(1);
    }
  };

  // Handle custom month range selection
  const handleCustomMonthRangeSelect = () => {
    if (customMonthRange.from && customMonthRange.to) {
      setMonthFilter("CUSTOM");
      setShowMonthPicker(false);
      setPage(1);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: "",
    });
    setSelectedProjects([]);
    setSelectedEngineers([]);
    setSelectedStatuses([]);
    setMonthFilter("ALL");
    setCustomMonthRange({ from: null, to: null });
    setSortConfig({
      field: "monthStartDate",
      direction: "desc"
    });
    setPage(1);
  };

  // Clear search only
  const clearSearch = () => {
    setFilters({ ...filters, search: "" });
    setPage(1);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Format month display
  const formatMonthDisplay = (monthStartDate, monthEndDate) => {
    if (!monthStartDate) return "-";
    const start = new Date(monthStartDate);
    const end = monthEndDate ? new Date(monthEndDate) : endOfMonth(start);

    const startMonth = start.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
    const endMonth = end.toLocaleDateString("en-GB", { month: "short", year: "numeric" });

    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return startMonth;
    }
    return `${startMonth} - ${endMonth}`;
  };

  // Get status display text
  const getStatusText = (status) => {
    if (!status) return "On Track";

    const statusStr = String(status).toUpperCase();

    switch (statusStr) {
      case "ONTRACK":
      case "ON TRACK":
        return "On Track";
      case "REPORTMISSING":
      case "REPORT MISSING":
        return "Report Missing";
      default:
        return statusStr === "REPORTMISSING" ? "Report Missing" : "On Track";
    }
  };

  // Handle download MPR with loading state
  const handleDownloadMPR = async (mpr) => {
    try {
      if (!mpr._id) {
        showSnackbar("No MPR report available for download", "warning");
        return;
      }

      setDownloadingId(mpr._id);

      const response = await axios.get(`${url}/api/mpr/pdf/${mpr._id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Generate filename
      const projectName = getProjectName(mpr).replace(/[^a-zA-Z0-9]/g, '_');
      const monthStr = formatMonthDisplay(mpr.monthStartDate, mpr.monthEndDate).replace(/[^a-zA-Z0-9]/g, '_');
      link.setAttribute('download', `MPR_${projectName}_${monthStr}.pdf`);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      showSnackbar("MPR report downloaded successfully!", "success");
    } catch (error) {
      console.error("Error downloading MPR:", error);
      showSnackbar("Failed to download MPR report. Please try again.", "error");
    } finally {
      setDownloadingId(null);
    }
  };

  // Handle edit MPR
  const handleEditMPR = (mpr) => {
    if (!mpr._id) {
      showSnackbar("Missing MPR information", "error");
      return;
    }

    // Get project ID
    const projectId = mpr.projectId?._id || mpr.projectId;

    if (!projectId) {
      showSnackbar("Project not found for this MPR", "error");
      return;
    }

    // Navigate to edit MPR page
    navigate(`/progress/edit-mpr/${projectId}`, {
      state: {
        projectId: projectId,
        monthStartDate: mpr.monthStartDate,
        monthEndDate: mpr.monthEndDate,
        projectName: mpr.projectName,
        mprId: mpr._id,
        fromMPRList: true,
        existingMPR: mpr,
        project: mpr.projectId // Pass the project object
      }
    });
  };

  // Handle add MPR
  const handleAddMPR = () => {
    navigate(`/progress/add-mpr`);
  };

  // Snackbar functions
  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Responsive table headers
  const getVisibleHeaders = () => {
    if (isMobile) {
      return headers.filter(header =>
        ["Project Name", "MPR Month", "Status", "Actions"].includes(header.key)
      );
    }
    if (isTablet) {
      return headers.filter(header =>
        !["Start Date", "End Date"].includes(header.key)
      );
    }
    return headers;
  };

  // Get project display name safely
  const getProjectName = (item) => {
    if (typeof item.projectId === 'object' && item.projectId !== null) {
      return item.projectId.projectName || item.projectName || "N/A";
    }
    return item.projectName || "N/A";
  };

  // Get site address safely
  const getSiteAddress = (item) => {
    if (typeof item.projectId === 'object' && item.projectId !== null) {
      return item.projectId.siteAddress || item.siteAddress || "-";
    }
    return item.siteAddress || "-";
  };

  // Get engineer name safely
  const getEngineerName = (item) => {
    return item.assignedEngineerName || "Unassigned";
  };

  // Get month display
  const getMonthDisplay = (item) => {
    if (item.monthStartDate) {
      return formatMonthDisplay(item.monthStartDate, item.monthEndDate);
    }
    return "-";
  };

  // Get status from projectStatus
  const getStatus = (item) => {
    return item.projectStatus || "ONTRACK";
  };

  // Responsive column rendering
  const renderTableCell = (header, item) => {
    const getFieldValue = (field) => {
      switch (field) {
        case "projectName":
          return getProjectName(item);
        case "projectStartDate":
          return formatDate(item.projectStartDate);
        case "projectEndDate":
          return formatDate(item.projectEndDate);
        case "assignedEngineerName":
          return getEngineerName(item);
        case "monthStartDate":
          return getMonthDisplay(item);
        case "projectStatus":
          return getStatusText(getStatus(item));
        default:
          return item[field] || "-";
      }
    };

    switch (header.key) {
      case "Project Name":
        return (
          <TableCell key={header.key}>
            <Box>
              <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                {getFieldValue("projectName")}
              </Typography>
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.MUTED_TEXT,
                }}
              >
                {getSiteAddress(item)}
              </Typography>
            </Box>
          </TableCell>
        );

      case "Start Date":
      case "End Date":
      case "Engineer":
      case "MPR Month":
        return (
          <TableCell key={header.key} sx={{ color: COLORS.TEXT, ...TYPOGRAPHY.BODY_LARGE }}>
            {getFieldValue(header.field)}
          </TableCell>
        );

      case "Status":
        const status = getStatus(item);
        const statusText = getStatusText(status);

        return (
          <TableCell key={header.key}>
            <Chip
              label={statusText}
              sx={{
                ...(statusStyles[status] || statusStyles["ONTRACK"]),
                ...TYPOGRAPHY.BODY_SMALL,
                height: 22,
                fontWeight: 500,
                borderRadius: 4,
                '& .MuiChip-label': {
                  px: 1.5,
                }
              }}
            />
          </TableCell>
        );

      case "Actions":
        return (
          <TableCell key={header.key}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>

              {/* Download button with loading */}
              <CustomTooltip title="Download MPR Report">
                <IconButton
                  size="small"
                  onClick={() => handleDownloadMPR(item)}
                  disabled={downloadingId === item._id}
                >
                  {downloadingId === item._id ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Icon name="download1" color={COLORS.PRIMARY} size={20} />
                  )}
                </IconButton>
              </CustomTooltip>

              {/* Edit button */}
              {(isAdminOrSuperAdmin) && (
                <CustomTooltip title="Edit MPR">
                  <IconButton
                    size="small"
                    onClick={() => handleEditMPR(item)}
                  >
                    <Icon name="Action3" color={COLORS.PRIMARY} size={20} />
                  </IconButton>
                </CustomTooltip>
              )}

            </Box>
          </TableCell>
        );

      default:
        return null;
    }
  };

  // Calculate displayed range for pagination
  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, totalMprs);

  // Get month filter display text
  const getMonthFilterDisplay = () => {
    if (monthFilter === "ALL") return "All Months";
    if (monthFilter === "THIS_MONTH") return "This Month";
    if (monthFilter === "LAST_MONTH") return "Last Month";
    if (monthFilter === "NEXT_MONTH") return "Next Month";
    if (monthFilter === "CUSTOM" && customMonthRange.from && customMonthRange.to) {
      const from = formatDate(customMonthRange.from);
      const to = formatDate(customMonthRange.to);
      return `${from} - ${to}`;
    }
    return "Select Month";
  };

  // Show loading while auth is loading
  if (authLoading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show login prompt if not authenticated
  if (!authLoading && !userData) {
    return (
      <Box sx={{
        p: 3,
        textAlign: 'center'
      }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          Please log in to view MPRs
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/login')}
          sx={{ mt: 2 }}
        >
          Go to Login
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            mb: 3,
            gap: { xs: 2, sm: 2 },
          }}
        >
          <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>
            Monthly Progress Reports (MPR)
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              ml: { sm: "auto" },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {/* Search Bar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: { xs: 1, sm: 'none' },
                minWidth: { xs: '100%', sm: 300 },
                px: 2,
                py: 0.7,
                borderRadius: 1.5,
                border: `1px solid ${COLORS.PRIMARY}`,
                backgroundColor: COLORS.BACKGROUND,
                color: COLORS.PRIMARY,
              }}
            >
              <Icon name="search" sx={{ fontSize: 18, color: "currentColor" }} />
              <InputBase
                placeholder="Search projects or MPRs..."
                value={filters.search}
                onChange={(e) => {
                  setFilters({ ...filters, search: e.target.value });
                  setPage(1);
                }}
                sx={{
                  flex: 1,
                  fontFamily: 'inherit',
                  ...TYPOGRAPHY.BODY_STANDARD,
                  color: COLORS.TEXT,
                  ml: 1,
                }}
              />
              {filters.search && (
                <IconButton
                  size="small"
                  onClick={clearSearch}
                  sx={{ padding: 0.5 }}
                >
                  <Icon name="close2" sx={{ fontSize: 14 }} color={COLORS.ERROR} />
                </IconButton>
              )}
            </Box>

            {/* Month Menu - Show active filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CustomTooltip title="Month Filter">
                <IconButton
                  onClick={(e) => setCalendarAnchorEl(e.currentTarget)}
                  sx={{
                    color: monthFilter !== "ALL" ? COLORS.PRIMARY : COLORS.SECONDARY,
                  }}
                >
                  <Icon name="calendar2" />
                </IconButton>
              </CustomTooltip>
            </Box>

            {/* Sort Menu - Show active filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CustomTooltip title="Sort Options">
                <IconButton
                  onClick={(e) => setSortAnchorEl(e.currentTarget)}
                  sx={{
                    color: sortConfig.direction !== "desc" ? COLORS.PRIMARY : COLORS.SECONDARY,
                  }}
                >
                  <Icon name="sort" />
                </IconButton>
              </CustomTooltip>
            </Box>

            {/* Filter Button - Toggles filter section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CustomTooltip title="Show Filters">
                <IconButton
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    color: showFilters ? COLORS.PRIMARY : COLORS.SECONDARY,
                  }}
                >
                  <Icon name="list" />
                </IconButton>
              </CustomTooltip>
            </Box>

            {/* Refresh / Clear */}
            <CustomTooltip title={isFilterApplied() ? "Clear Filters" : "Refresh"}>
              <IconButton
                onClick={isFilterApplied() ? clearFilters : fetchMPRs}
              >
                <Icon
                  name="rotate"
                  color={isFilterApplied() ? COLORS.ERROR : COLORS.SECONDARY}
                />
              </IconButton>
            </CustomTooltip>
          </Box>
        </Box>

        {/* Mobile Add MPR Button */}
        {(isAdminOrSuperAdmin || isEngineerRole) && (
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'center', mb: 2 }}>
            <Button
              startIcon={<Icon name="plus2" />}
              sx={{
                bgcolor: COLORS.SECONDARY,
                color: "#fff",
                textTransform: "none",
                borderRadius: 2,
                ...TYPOGRAPHY.H4,
                px: 2.5,
                "&:hover": { bgcolor: COLORS.SECONDARY },
                width: '100%',
              }}
              onClick={handleAddMPR}
            >
              Add New MPR
            </Button>
          </Box>
        )}

        {/* Filter Section - Horizontal Boxes (Only shown when showFilters is true) */}
        {showFilters && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
            alignItems: 'flex-start',
            flexWrap: 'wrap'
          }}>
            {/* Project Filter */}
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.PRIMARY,
                  mb: 0.5,
                  fontWeight: 600
                }}
              >
                Project
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  value={selectedProjects}
                  onChange={(e) => {
                    setSelectedProjects(e.target.value);
                    setPage(1);
                  }}
                  open={filterMenusOpen.projects}
                  onOpen={() => setFilterMenusOpen(prev => ({ ...prev, projects: true }))}
                  onClose={() => setFilterMenusOpen(prev => ({ ...prev, projects: false }))}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return (
                        <Typography sx={{
                          color: COLORS.LIGHT_SECONDARY,
                          fontSize: '0.875rem'
                        }}>
                          Select Projects
                        </Typography>
                      );
                    }
                    return selected.length <= 2
                      ? selected.map(id => {
                        const project = projects.find(p => p._id === id);
                        return project?.projectName || (id === state?.projectId ? state?.projectName : id);
                      }).join(', ')
                      : `${selected.length} selected`;
                  }}
                  displayEmpty
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 250,
                        width: 280,
                        overflow: 'auto',
                        mt: 0.5,
                        '& .MuiMenuItem-root': {
                          minHeight: 40,
                          padding: '6px 12px',
                        },
                        '& .MuiCheckbox-root': {
                          padding: '6px',
                        }
                      }
                    },
                  }}
                >
                  {projects.length > 0 ? (
                    projects.map((project) => (
                      <MenuItem
                        key={project._id}
                        value={project._id}
                      >
                        <Checkbox
                          checked={selectedProjects.indexOf(project._id) > -1}
                          size="small"
                        />
                        <ListItemText
                          primary={project.projectName}
                          sx={{
                            margin: 0,
                            '& .MuiTypography-root': {
                              fontSize: '0.85rem',
                            }
                          }}
                        />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <Typography sx={{
                        fontSize: '0.85rem',
                        color: COLORS.TEXT,
                        fontStyle: 'italic'
                      }}>
                        No projects available
                      </Typography>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>

            {/* Engineer Filter - Only show for non-engineer roles */}
            {!isEngineerRole && (
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.BODY_SMALL,
                    color: COLORS.PRIMARY,
                    mb: 0.5,
                    fontWeight: 600
                  }}
                >
                  Engineer
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    multiple
                    value={selectedEngineers}
                    onChange={(e) => {
                      setSelectedEngineers(e.target.value);
                      setPage(1);
                    }}
                    open={filterMenusOpen.engineers}
                    onOpen={() => setFilterMenusOpen(prev => ({ ...prev, engineers: true }))}
                    onClose={() => setFilterMenusOpen(prev => ({ ...prev, engineers: false }))}
                    input={<OutlinedInput />}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return (
                          <Typography sx={{
                            color: COLORS.LIGHT_SECONDARY,
                            fontSize: '0.875rem'
                          }}>
                            Select Engineers
                          </Typography>
                        );
                      }
                      return selected.length <= 2
                        ? selected.map(id => {
                          const engineer = engineers.find(e => e._id === id);
                          return engineer?.name || id;
                        }).join(', ')
                        : `${selected.length} selected`;
                    }}
                    displayEmpty
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 250,
                          width: 280,
                          overflow: 'auto',
                          mt: 0.5,
                          '& .MuiMenuItem-root': {
                            minHeight: 40,
                            padding: '6px 12px',
                          },
                          '& .MuiCheckbox-root': {
                            padding: '6px',
                          }
                        }
                      },
                    }}
                  >
                    {engineers.length > 0 ? (
                      engineers.map((engineer) => (
                        <MenuItem
                          key={engineer._id}
                          value={engineer._id}
                        >
                          <Checkbox
                            checked={selectedEngineers.indexOf(engineer._id) > -1}
                            size="small"
                          />
                          <ListItemText
                            primary={engineer.name}
                            sx={{
                              margin: 0,
                              '& .MuiTypography-root': {
                                fontSize: '0.85rem',
                              }
                            }}
                          />
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        <Typography sx={{
                          fontSize: '0.85rem',
                          color: COLORS.TEXT,
                          fontStyle: 'italic'
                        }}>
                          No engineers available
                        </Typography>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Status Filter */}
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.PRIMARY,
                  mb: 0.5,
                  fontWeight: 600
                }}
              >
                Status
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  value={selectedStatuses}
                  onChange={(e) => {
                    setSelectedStatuses(e.target.value);
                    setPage(1);
                  }}
                  open={filterMenusOpen.statuses}
                  onOpen={() => setFilterMenusOpen(prev => ({ ...prev, statuses: true }))}
                  onClose={() => setFilterMenusOpen(prev => ({ ...prev, statuses: false }))}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return (
                        <Typography sx={{
                          color: COLORS.LIGHT_SECONDARY,
                          fontSize: '0.875rem'
                        }}>
                          Select Statuses
                        </Typography>
                      );
                    }
                    return selected.length <= 2
                      ? selected.map(s => getStatusText(s)).join(', ')
                      : `${selected.length} selected`;
                  }}
                  displayEmpty
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 250,
                        width: 280,
                        overflow: 'auto',
                        mt: 0.5,
                        '& .MuiMenuItem-root': {
                          minHeight: 40,
                          padding: '6px 12px',
                        },
                        '& .MuiCheckbox-root': {
                          padding: '6px',
                        }
                      }
                    },
                  }}
                >
                  {["ONTRACK", "REPORTMISSING"].map((status) => (
                    <MenuItem
                      key={status}
                      value={status}
                    >
                      <Checkbox
                        checked={selectedStatuses.indexOf(status) > -1}
                        size="small"
                      />
                      <ListItemText
                        primary={getStatusText(status)}
                        sx={{
                          margin: 0,
                          '& .MuiTypography-root': {
                            fontSize: '0.85rem',
                          }
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}

        {/* Error Message */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table - Responsive Container */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: "transparent",
            overflow: "auto",
            width: "100%"
          }}
        >
          <TableContainer>
            <Table
              sx={{
                backgroundColor: "transparent",
                minWidth: isMobile ? 500 : "auto",
                "& thead th": {
                  backgroundColor: "transparent",
                },
                "& tbody td": {
                  backgroundColor: "transparent",
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {getVisibleHeaders().map((header) => (
                    <TableCell
                      key={header.key}
                      sx={{
                        ...TYPOGRAPHY.H4,
                        color: COLORS.PRIMARY,
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        whiteSpace: "nowrap",
                        py: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <span>{header.key}</span>
                        {header.sortable && (
                          <CustomTooltip title={
                            sortConfig.field === header.field
                              ? `Sorted ${sortConfig.direction === 'asc' ? 'Ascending' : 'Descending'}`
                              : `Sort by ${header.key}`
                          }>
                            <IconButton
                              onClick={() => handleSort(header.field)}
                              sx={{
                                padding: 0,
                                width: 24,
                                height: 24,
                              }}
                            >
                              <Icon
                                name={getSortIcon(header.field)}
                                color={
                                  sortConfig.field === header.field
                                    ? COLORS.ERROR
                                    : COLORS.LIGHT_SECONDARY
                                }
                              />
                            </IconButton>
                          </CustomTooltip>
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={getVisibleHeaders().length} align="center" sx={{ py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : mprs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={getVisibleHeaders().length} align="center" sx={{ py: 3 }}>
                      <Typography sx={{ color: COLORS.TEXT }}>
                        {isFilterApplied()
                          ? "No MPRs found matching your filters"
                          : "No MPRs found"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  mprs.slice((page - 1) * pageSize, page * pageSize).map((mpr) => (
                    <TableRow
                      key={mpr._id}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        }
                      }}
                    >
                      {getVisibleHeaders().map((header) => renderTableCell(header, mpr))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {!loading && mprs.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
                p: 2,
              }}
            >
              <Typography sx={{ fontSize: 13, color: "#777" }}>
                {startIndex} - {endIndex} of {totalMprs}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CustomTooltip title="Previous">
                  <span>
                    <IconButton
                      onClick={() => setPage(prev => Math.max(1, prev - 1))}
                      disabled={page === 1}
                      sx={{
                        width: 32,
                        height: 32,
                        color: page === 1 ? COLORS.LIGHT_SECONDARY : COLORS.PRIMARY,
                      }}
                    >
                      <Icon name="chevron-left" />
                    </IconButton>
                  </span>
                </CustomTooltip>

                {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 3) {
                    pageNum = i + 1;
                  } else if (page === 1) {
                    pageNum = i + 1;
                  } else if (page === totalPages) {
                    pageNum = totalPages - 2 + i;
                  } else {
                    pageNum = page - 1 + i;
                  }

                  if (pageNum > 0 && pageNum <= totalPages) {
                    return (
                      <Button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        sx={{
                          minWidth: 32,
                          height: 32,
                          fontSize: 12,
                          fontWeight: 500,
                          bgcolor: page === pageNum ? COLORS.PRIMARY : "transparent",
                          color: page === pageNum ? "#fff" : COLORS.TEXT,
                          border: page === pageNum
                            ? "none"
                            : `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          borderRadius: 1,
                          "&:hover": {
                            bgcolor: page === pageNum
                              ? COLORS.PRIMARY
                              : COLORS.LIGHT_BACKGROUND,
                          },
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  }
                  return null;
                })}

                <CustomTooltip title="Next">
                  <span>
                    <IconButton
                      onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={page === totalPages}
                      sx={{
                        width: 32,
                        height: 32,
                        color: page === totalPages ? COLORS.LIGHT_SECONDARY : COLORS.PRIMARY,
                      }}
                    >
                      <Icon name="chevron-right" />
                    </IconButton>
                  </span>
                </CustomTooltip>
              </Box>
            </Box>
          )}
        </Paper>

        {/* Global Sort Menu */}
        <Menu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={() => setSortAnchorEl(null)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
              px: 0.5,
              minWidth: 160,
            },
          }}
        >
          {[
            { label: "Latest First", value: "LATEST", icon: "foldUp" },
            { label: "Oldest First", value: "OLDEST", icon: "foldDown" },
            { label: "Project Name A-Z", value: "AZ", icon: "sort" },
            { label: "Project Name Z-A", value: "ZA", icon: "sort" },
          ].map((item) => (
            <MenuItem
              key={item.value}
              onClick={() => handleGlobalSort(item.value)}
              sx={{
                gap: 1.5,
                borderRadius: 1,
                "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
              }}
            >
              <Icon name={item.icon} color={COLORS.TEXT} />
              <Typography sx={TYPOGRAPHY.BODY_LARGE}>{item.label}</Typography>
            </MenuItem>
          ))}
        </Menu>

        {/* Month Menu */}
        <Menu
          anchorEl={calendarAnchorEl}
          open={Boolean(calendarAnchorEl)}
          onClose={() => setCalendarAnchorEl(null)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
              px: 0.5,
            },
          }}
        >
          {[
            { label: "All Months", value: "ALL", icon: "calendarAll" },
            { label: "This Month", value: "THIS_MONTH", icon: "calendarToday" },
            { label: "Last Month", value: "LAST_MONTH", icon: "calendarYesterday" },
            { label: "Next Month", value: "NEXT_MONTH", icon: "calendarTomorrow" },
            { label: "Custom Month Range", value: "CUSTOM", icon: "calendarCustom" },
          ].map((item) => {
            const isActive = monthFilter === item.value;

            return (
              <MenuItem
                key={item.value}
                onClick={() => handleMonthFilterSelect(item.value)}
                sx={{
                  gap: 1.5,
                  borderRadius: 1,
                  color: isActive ? COLORS.PRIMARY : COLORS.TEXT,
                }}
              >
                <Icon
                  name={item.icon}
                  size={18}
                  color={isActive ? COLORS.PRIMARY : COLORS.TEXT}
                />
                <Typography sx={TYPOGRAPHY.BODY_LARGE}>
                  {item.label}
                </Typography>
              </MenuItem>
            );
          })}
        </Menu>

        {/* Custom Month Range Picker Dialog */}
        <Dialog
          open={showMonthPicker}
          onClose={() => setShowMonthPicker(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="calendarCustom" color={COLORS.PRIMARY} />
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
                Select Month Range
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <DatePicker
                label="From Date"
                value={customMonthRange.from}
                onChange={(newValue) => {
                  setCustomMonthRange(prev => ({ ...prev, from: newValue }));
                }}
                slotProps={{ textField: { fullWidth: true } }}
                maxDate={customMonthRange.to || new Date()}
              />
              <DatePicker
                label="To Date"
                value={customMonthRange.to}
                onChange={(newValue) => {
                  setCustomMonthRange(prev => ({ ...prev, to: newValue }));
                }}
                slotProps={{ textField: { fullWidth: true } }}
                minDate={customMonthRange.from}
                maxDate={new Date()}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setShowMonthPicker(false)}
              sx={{
                color: COLORS.TEXT,
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCustomMonthRangeSelect}
              disabled={!customMonthRange.from || !customMonthRange.to}
              sx={{
                bgcolor: COLORS.SECONDARY,
                color: "#fff",
                "&:hover": { bgcolor: COLORS.SECONDARY },
                "&:disabled": { bgcolor: COLORS.LIGHT_SECONDARY }
              }}
            >
              Apply Month Range
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default MPR;